import React, { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import PageShell from "@/components/PageShell";
import NextRoom from "@/components/NextRoom";
import { OBSERVATIONS, FIELD_NOTES, DOMAINS } from "@/data/content";

export default function Thinking() {
  const [params, setParams] = useSearchParams();
  const activeDomain = params.get("domain") || "all";

  const filtered = useMemo(() => {
    if (activeDomain === "all") return OBSERVATIONS;
    return OBSERVATIONS.filter((o) => o.domain === activeDomain);
  }, [activeDomain]);

  const fieldNotesFiltered = useMemo(() => {
    if (activeDomain === "all") return FIELD_NOTES;
    return FIELD_NOTES.filter((n) => n.domain === activeDomain);
  }, [activeDomain]);

  const setDomain = (d) => {
    if (d === "all") {
      params.delete("domain");
    } else {
      params.set("domain", d);
    }
    setParams(params, { replace: true });
  };

  return (
    <PageShell
      testId="page-thinking"
      title="Thinking | Quentin Young"
      description="Field notes, observations, and essays on human nature, perception, purpose, achievement, culture, leadership, systems, organizations, craft, and relationships."
      ogImage="/og-thinking.jpg"
      breadcrumbs={[{ name: "Home", url: "/" }, { name: "Thinking", url: "/thinking" }]}
    >
      <section className="container-edge pt-16 md:pt-24 pb-16">
        <p className="eyebrow-gold mb-5">Thinking · Observation Archive</p>
        <h1 className="display-serif text-4xl md:text-6xl lg:text-7xl text-paper-primary text-balance">
          The beliefs that survived contact with reality.
        </h1>
        <p className="mt-8 max-w-2xl font-serif text-lg md:text-xl text-paper-secondary leading-relaxed text-pretty">
          Cornerstones earn permanence. Field notes are current. Filter if you must.
          Wander if you can.
        </p>
      </section>

      {/* Filters */}
      <section className="border-y border-ink-line bg-ink-surface/40 sticky top-16 md:top-20 z-30 backdrop-blur-sm">
        <div className="container-edge py-4 overflow-x-auto">
          <div className="flex items-center gap-2 min-w-max">
            <p className="eyebrow mr-2">Domain</p>
            <button
              type="button"
              onClick={() => setDomain("all")}
              data-testid="filter-all"
              className={`font-mono text-[10px] uppercase tracking-[0.22em] px-3 py-2 border ${
                activeDomain === "all" ? "border-gold text-gold-warm" : "border-ink-line text-paper-secondary hover:text-paper-primary"
              }`}
            >
              All
            </button>
            {DOMAINS.map((d) => (
              <button
                key={d.slug}
                type="button"
                onClick={() => setDomain(d.slug)}
                data-testid={`filter-${d.slug}`}
                className={`font-mono text-[10px] uppercase tracking-[0.22em] px-3 py-2 border ${
                  activeDomain === d.slug ? "border-gold text-gold-warm" : "border-ink-line text-paper-secondary hover:text-paper-primary"
                }`}
              >
                {d.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Cornerstones list */}
      <section className="bg-ink-base py-16 md:py-24">
        <div className="container-edge">
          <div className="flex items-baseline justify-between mb-10">
            <p className="eyebrow">Cornerstones</p>
            <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-paper-mute">
              {filtered.length} entr{filtered.length === 1 ? "y" : "ies"}
            </p>
          </div>
          {filtered.length === 0 ? (
            <p className="text-paper-tertiary py-16">No entries yet in this territory. Wander elsewhere.</p>
          ) : (
            <ol className="space-y-px bg-ink-line">
              {filtered.map((o, i) => {
                const d = DOMAINS.find((x) => x.slug === o.domain);
                return (
                  <li key={o.slug} className="bg-ink-base">
                    <Link
                      to={`/thinking/${o.slug}`}
                      data-testid={`thinking-row-${o.slug}`}
                      className="group grid grid-cols-12 gap-6 py-10 md:py-14 px-2 md:px-4 items-baseline hover:bg-ink-surface/40 transition-colors duration-500"
                    >
                      <div className="col-span-12 md:col-span-2">
                        <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-paper-mute">
                          {String(i + 1).padStart(2, "0")} · {d?.name}
                        </p>
                      </div>
                      <div className="col-span-12 md:col-span-9">
                        <h3 className="display-serif text-2xl md:text-4xl text-paper-primary group-hover:text-gold-warm transition-colors duration-300 text-balance leading-[1.05]">
                          {o.headline}
                        </h3>
                        <p className="mt-3 max-w-3xl text-paper-secondary font-serif text-base md:text-lg leading-relaxed text-pretty">
                          {o.body}
                        </p>
                      </div>
                      <div className="col-span-12 md:col-span-1 md:text-right font-mono text-[10px] uppercase tracking-[0.24em] text-paper-tertiary group-hover:text-paper-primary">
                        Read →
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      </section>

      {/* Field Notes */}
      <section className="bg-ink-surface border-t border-ink-line py-16 md:py-24">
        <div className="container-edge">
          <div className="flex items-baseline justify-between mb-10">
            <p className="eyebrow">Field Notes</p>
            <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-paper-mute">
              {fieldNotesFiltered.length} note{fieldNotesFiltered.length === 1 ? "" : "s"}
            </p>
          </div>
          {fieldNotesFiltered.length === 0 ? (
            <p className="text-paper-tertiary py-8">No field notes here yet.</p>
          ) : (
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-px bg-ink-line">
              {fieldNotesFiltered.map((n) => {
                const d = DOMAINS.find((x) => x.slug === n.domain);
                return (
                  <li key={n.slug} className="bg-ink-surface p-8 md:p-10" data-testid={`field-note-${n.slug}`}>
                    <p className="eyebrow mb-3">{d?.name}</p>
                    <p className="font-serif italic text-xl text-paper-primary text-pretty leading-snug">{n.text}</p>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </section>

      <NextRoom
        eyebrow="Move to the next room"
        title="Thinking meets reality here."
        rooms={[
          { label: "See these ideas in practice", to: "/building", hint: "proof" },
          { label: "Study the framework beneath them", to: "/philosophy", hint: "framework" },
          { label: "Trace the evidence", to: "/artifacts", hint: "long view" },
        ]}
        testId="thinking-next"
      />
    </PageShell>
  );
}
